<!-- src/components/widgets/CalendarWidget.vue -->
<template>
  <div class="calendar-widget">
    <CalendarHeader
      :event-count="upcomingEvents.length"
      :is-offline="isOffline"
      :has-error="!!error"
      :loading="loading"
      :show-form="showForm"
      @retry="handleRetry"
      @toggle-form="toggleForm"
    />

    <!-- Create / Edit Event Form -->
    <CalendarEventForm
      v-if="showForm"
      :form="form"
      :editing-id="editingId"
      :loading="loading"
      @update:form="form = $event"
      @submit="handleSubmit"
      @cancel="resetForm"
    />

    <!-- Error banner (cached events still visible) -->
    <CalendarStatusHero
      v-if="error && displayedEvents.length"
      type="banner"
      :loading="loading"
      @retry="handleRetry"
    />

    <!-- Loading (no cache) -->
    <CalendarStatusHero
      v-if="loading && !events.length"
      type="loading"
    />

    <!-- Full error hero (no cache at all) -->
    <CalendarStatusHero
      v-else-if="error && !displayedEvents.length"
      type="error"
      :loading="loading"
      @retry="handleRetry"
    />

    <!-- Events list -->
    <ul v-else-if="displayedEvents.length" class="events-list">
      <CalendarEventCard
        v-for="event in displayedEvents"
        :key="event.id"
        :event="event"
        :is-expanded="expandedId === event.id"
        :current-time="currentTime"
        @toggle-expand="toggleExpand"
        @edit="startEdit"
        @delete="handleDelete"
      />
    </ul>

    <!-- Empty state -->
    <CalendarStatusHero v-else-if="!loading" type="empty" />

    <!-- Show more / less footer -->
    <div v-if="upcomingEvents.length > EVENT_LIMIT" class="list-footer">
      <button class="btn-show-more" @click="showAll = !showAll">
        {{ showAll ? 'Show less' : `Show all (${upcomingEvents.length})` }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useCalendarEvents } from '@/composables/useCalendarEvents';

import CalendarHeader from './calendar/CalendarHeader.vue';
import CalendarEventForm from './calendar/CalendarEventForm.vue';
import CalendarEventCard from './calendar/CalendarEventCard.vue';
import CalendarStatusHero from './calendar/CalendarStatusHero.vue';

const { events, loading, error, fetchEvents, addEvent, updateEvent, deleteEvent } = useCalendarEvents();

const showForm = ref(false);
const editingId = ref(null);
const expandedId = ref(null);
const showAll = ref(false);
const currentTime = ref(new Date());
const isOffline = ref(false);

const LOCAL_CACHE_KEY = 'calendar_events_backup';
let tickerTimer = null;
const EVENT_LIMIT = 5;

const form = reactive({
  title: '',
  description: '',
  startTime: '',
  isAllDay: false,
});

// Cache successful fetches locally
watch(events, (newEvents) => {
  if (newEvents && newEvents.length > 0) {
    localStorage.setItem(LOCAL_CACHE_KEY, JSON.stringify(newEvents));
  }
}, { deep: true });

const loadCalendarData = async (forceRefresh = false) => {
  const cached = localStorage.getItem(LOCAL_CACHE_KEY);

  if (cached && !forceRefresh && !events.value.length) {
    try {
      events.value = JSON.parse(cached);
    } catch (e) {
      console.error('Failed to parse cached calendar events', e);
    }
  }

  try {
    await fetchEvents();
    isOffline.value = false;
  } catch (err) {
    if (localStorage.getItem(LOCAL_CACHE_KEY)) {
      isOffline.value = true;
    }
  }
};

const handleRetry = () => loadCalendarData(true);

onMounted(() => {
  loadCalendarData();
  tickerTimer = setInterval(() => {
    currentTime.value = new Date();
  }, 30000);
});

onUnmounted(() => {
  if (tickerTimer) clearInterval(tickerTimer);
});

const upcomingEvents = computed(() => {
  if (!events.value?.length) return [];
  const now = currentTime.value;

  return [...events.value]
    .filter(event => {
      if (!event.startTime) return false;
      if (event.isAllDay) {
        const [datePart] = event.startTime.split('T');
        const [year, month, day] = datePart.split('-');
        return new Date(year, month - 1, day, 23, 59, 59, 999) >= now;
      }
      return new Date(event.startTime) >= now;
    })
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
});

const displayedEvents = computed(() =>
  showAll.value ? upcomingEvents.value : upcomingEvents.value.slice(0, EVENT_LIMIT)
);

const toggleExpand = (id) => {
  expandedId.value = expandedId.value === id ? null : id;
};

const toggleForm = () => {
  if (showForm.value) {
    resetForm();
  } else {
    showForm.value = true;
  }
};

const startEdit = (event) => {
  editingId.value = event.id;
  form.title = event.title;
  form.description = event.description || '';
  form.isAllDay = Boolean(event.isAllDay);

  if (event.startTime) {
    if (event.isAllDay) {
      form.startTime = event.startTime.split('T')[0];
    } else {
      const d = new Date(event.startTime);
      const tzOffset = d.getTimezoneOffset() * 60000;
      form.startTime = new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
    }
  } else {
    form.startTime = '';
  }

  showForm.value = true;
};

const resetForm = () => {
  editingId.value = null;
  form.title = '';
  form.description = '';
  form.startTime = '';
  form.isAllDay = false;
  showForm.value = false;
};

const handleSubmit = async () => {
  if (!form.title || !form.startTime) return;

  const payload = {
    title: form.title,
    description: form.description,
    startTime: form.startTime,
    isAllDay: form.isAllDay,
  };

  try {
    if (editingId.value) {
      await updateEvent(editingId.value, payload);
    } else {
      await addEvent(payload);
    }
    resetForm();
  } catch (err) {
    // Error handled via composable state
  }
};

const handleDelete = async (id) => {
  if (confirm('Are you sure you want to delete this event?')) {
    try {
      await deleteEvent(id);
      if (editingId.value === id) resetForm();
      if (expandedId.value === id) expandedId.value = null;
    } catch (err) {
      // Error handled via composable state
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/styles/abstract/variables" as *;

.calendar-widget {
  background: #ffffff;
  border: 1px solid color.mix($light-brown, #ffffff, 40%);
  border-radius: 16px;
  padding: 1.75rem;
  width: 100%;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01);

  .events-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .list-footer {
    display: flex;
    justify-content: center;
    margin-top: 1.25rem;

    .btn-show-more {
      background: transparent;
      border: none;
      color: $dark-blue;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      padding: 0.4rem 0.8rem;
      border-radius: 6px;

      &:hover {
        background: color.mix($white, #ffffff, 60%);
        text-decoration: underline;
      }
    }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.25s ease-in-out;
  max-height: 250px;
  opacity: 1;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
}
</style>